import pluralize from 'pluralize';
import { memo, useCallback, useMemo } from 'react';

import { MainSideNavOverlayTraySectionHeader } from '~/components/v2/components/workspace/main-side-nav/MainSideNavOverlayTraySectionHeader';
import { MainSideNavOverlayTrayVirtualizedList } from '~/components/v2/components/workspace/main-side-nav/MainSideNavOverlayTrayVirtualizedList';
import {
  buildWorkspaceImportsVirtualRows,
  estimateWorkspaceImportsVirtualRowSize,
  getWorkspaceImportsVirtualRowKey,
  type WorkspaceImportsVirtualRow,
} from '~/components/v2/components/workspace/workspace-imports/buildWorkspaceImportsVirtualRows';
import { WorkspaceImportsBanner } from '~/components/v2/components/workspace/workspace-imports/WorkspaceImportsBanner';
import { WorkspaceImportsCompletedListItem } from '~/components/v2/components/workspace/workspace-imports/WorkspaceImportsCompletedListItem';
import { WorkspaceImportsRow } from '~/components/v2/components/workspace/workspace-imports/WorkspaceImportsRow';
import {
  type WorkspaceImportRow,
  type WorkspaceImportsBanner as WorkspaceImportsBannerModel,
} from '~/components/v2/components/workspace/workspace-imports/workspaceImportTypes';

export type WorkspaceImportsVirtualizedListProps = {
  banner: WorkspaceImportsBannerModel | null;
  rows: WorkspaceImportRow[];
};

export const WorkspaceImportsVirtualizedList = memo(({ banner, rows }: WorkspaceImportsVirtualizedListProps) => {
  const virtualRows = useMemo(() => buildWorkspaceImportsVirtualRows({ banner, rows }), [banner, rows]);

  const renderRow = useCallback((row: WorkspaceImportsVirtualRow) => {
    switch (row.type) {
      case 'banner':
        return <WorkspaceImportsBanner banner={row.banner} />;
      case 'inProgress':
        return (
          <div className={row.withLeadingGap ? 'pt-2' : undefined}>
            <WorkspaceImportsRow row={row.row} />
          </div>
        );
      case 'failedHeader':
        return (
          <MainSideNavOverlayTraySectionHeader
            className={row.withSectionGap ? 'pt-2' : undefined}
            headerTestId="WORKSPACE_IMPORTS_FAILED_HEADER"
            title={pluralize('error', row.count, true)}
          />
        );
      case 'failed':
      case 'cancelled':
        return <WorkspaceImportsRow row={row.row} />;
      case 'cancelledHeader':
        return (
          <MainSideNavOverlayTraySectionHeader
            className={row.withSectionGap ? 'pt-2' : undefined}
            headerTestId="WORKSPACE_IMPORTS_CANCELLED_HEADER"
            title={pluralize('cancelled import', row.count, true)}
          />
        );
      case 'completedHeader':
        return (
          <MainSideNavOverlayTraySectionHeader
            className={row.withSectionGap ? 'pt-2' : undefined}
            headerTestId="WORKSPACE_IMPORTS_COMPLETED_HEADER"
            title={`${row.count} completed`}
          />
        );
      case 'completed':
        return <WorkspaceImportsCompletedListItem row={row.row} />;
    }
  }, []);

  return (
    <MainSideNavOverlayTrayVirtualizedList
      estimateSize={estimateWorkspaceImportsVirtualRowSize}
      getRowKey={getWorkspaceImportsVirtualRowKey}
      renderRow={renderRow}
      rows={virtualRows}
      testId="WORKSPACE_IMPORTS_LIST"
    />
  );
});

WorkspaceImportsVirtualizedList.displayName = 'WorkspaceImportsVirtualizedList';
